import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Pencil,
  Check,
  X,
  Trash2,
  ArrowUp,
  ArrowDown,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import type {
  ApprovalChainDto,
  ApprovalConditionDto,
  ApprovalConditionSetDto,
  ApprovalLevelDto,
  ConditionField,
  OrgDimensionDto,
  UpdateApprovalLevelRequest,
  UserDto,
  DocumentType,
} from "@procureflow/shared-types";
import { PageHeader } from "@/components/page-header";
import { fetchWithAuth } from "@/lib/auth-client";
import { useAuth } from "@/contexts/auth-context";

export const Route = createFileRoute("/settings/approvals")({
  head: () => ({
    meta: [
      { title: "Approval Configuration — ProcureFlow" },
      {
        name: "description",
        content: "Configure per-document-type approval Condition Sets.",
      },
    ],
  }),
  component: ApprovalChainsPage,
});

const DOC_LABEL: Record<DocumentType, string> = {
  PR: "Purchase Requests",
  PO: "Purchase Orders",
  RFQ: "Requests for Quotation",
  GRN: "Goods Receipt Notes",
  Invoice: "Invoices",
};

// US-061 — no currency symbol; grouped 2-decimal (matches @/lib/format).
function formatAmount(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function ApprovalChainsPage() {
  const { user } = useAuth();
  const [chains, setChains] = useState<ApprovalChainDto[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);
  const [locations, setLocations] = useState<OrgDimensionDto[]>([]);
  const [departments, setDepartments] = useState<OrgDimensionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set(["PR"]));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      setLoading(true);
      setError(null);
      try {
        const [chainsRes, usersRes, locRes, deptRes] = await Promise.all([
          fetchWithAuth("/api/approval-chains"),
          fetchWithAuth("/api/users"),
          fetchWithAuth("/api/locations?active_only=true"),
          fetchWithAuth("/api/departments?active_only=true"),
        ]);
        if (!chainsRes.ok) {
          const body = await chainsRes.json().catch(() => ({}));
          if (!cancelled) setError(body.message ?? "Failed to load approval configuration.");
          return;
        }
        const { data } = (await chainsRes.json()) as { data: ApprovalChainDto[] };
        const usersData = usersRes.ok ? ((await usersRes.json()) as { data: UserDto[] }).data : [];
        const locData = locRes.ok
          ? ((await locRes.json()) as { data: OrgDimensionDto[] }).data
          : [];
        const deptData = deptRes.ok
          ? ((await deptRes.json()) as { data: OrgDimensionDto[] }).data
          : [];
        if (!cancelled) {
          setChains(data);
          setUsers(usersData.filter((u) => u.status === "Active"));
          setLocations(locData);
          setDepartments(deptData);
        }
      } catch {
        if (!cancelled) setError("An unexpected error occurred.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function replaceChain(updated: ApprovalChainDto) {
    setChains((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  }

  if (user?.role !== "Admin") {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Approval Configuration"
          description="You don't have access to approval configuration."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Approval Configuration"
        description="Configure an ordered list of Condition Sets for each document type."
      />

      {loading && <div className="h-24 rounded-2xl bg-muted/30 animate-pulse" />}
      {error && (
        <div className="rounded-lg bg-destructive/10 border border-destructive/20 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {!loading &&
        !error &&
        chains.map((chain) => (
          <ChainSection
            key={chain.id}
            chain={chain}
            users={users}
            locations={locations}
            departments={departments}
            expanded={open.has(chain.document_type)}
            onToggle={() =>
              setOpen((prev) => {
                const next = new Set(prev);
                if (next.has(chain.document_type)) next.delete(chain.document_type);
                else next.add(chain.document_type);
                return next;
              })
            }
            onChange={replaceChain}
          />
        ))}
    </div>
  );
}

function ChainSection({
  chain,
  users,
  locations,
  departments,
  expanded,
  onToggle,
  onChange,
}: {
  chain: ApprovalChainDto;
  users: UserDto[];
  locations: OrgDimensionDto[];
  departments: OrgDimensionDto[];
  expanded: boolean;
  onToggle: () => void;
  onChange: (c: ApprovalChainDto) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [emailDraft, setEmailDraft] = useState(chain.escalation_email ?? "");

  useEffect(() => {
    setEmailDraft(chain.escalation_email ?? "");
  }, [chain.escalation_email]);

  async function mutate(promise: Promise<Response>, successMsg: string): Promise<boolean> {
    setBusy(true);
    try {
      const res = await promise;
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(body.message ?? "Action failed.");
        return false;
      }
      onChange(body.data as ApprovalChainDto);
      toast.success(successMsg);
      return true;
    } finally {
      setBusy(false);
    }
  }

  async function saveEscalationEmail() {
    const trimmed = emailDraft.trim();
    await mutate(
      fetchWithAuth(`/api/approval-chains/${chain.id}/escalation-email`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ escalation_email: trimmed === "" ? null : trimmed }),
      }),
      "Escalation email saved",
    );
  }

  async function addConditionSet() {
    await mutate(
      fetchWithAuth(`/api/approval-chains/${chain.id}/condition-sets`, { method: "POST" }),
      "Condition Set added",
    );
  }

  async function deleteConditionSet(setId: string) {
    await mutate(
      fetchWithAuth(`/api/approval-chains/${chain.id}/condition-sets/${setId}`, {
        method: "DELETE",
      }),
      "Condition Set removed",
    );
  }

  async function reorderSet(setId: string, direction: -1 | 1) {
    const ids = chain.condition_sets.map((s) => s.id);
    const idx = ids.indexOf(setId);
    const swap = idx + direction;
    if (swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    await mutate(
      fetchWithAuth(`/api/approval-chains/${chain.id}/condition-sets/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ condition_set_ids: ids }),
      }),
      "Condition Sets reordered",
    );
  }

  return (
    <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span className="font-medium">{DOC_LABEL[chain.document_type as DocumentType]}</span>
          <span className="text-xs text-muted-foreground">
            ({chain.condition_sets.length} condition set
            {chain.condition_sets.length !== 1 ? "s" : ""}
            {chain.is_value_bearing ? ", value-bearing" : ", flat"})
          </span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border px-5 py-4 space-y-5">
          {/* Escalation email */}
          <div className="flex flex-wrap items-end gap-3">
            <label className="space-y-1 text-xs">
              <span className="text-muted-foreground">Escalation email</span>
              <input
                aria-label="Escalation email"
                type="email"
                placeholder="none"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                className="block w-64 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              />
            </label>
            <button
              type="button"
              disabled={busy}
              onClick={saveEscalationEmail}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted/50 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" /> Save email
            </button>
          </div>

          {chain.condition_sets.length === 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-[var(--info)]/40 bg-[var(--info)]/10 px-3 py-2 text-sm [color:var(--info)]">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              This chain has no Condition Sets — documents of this type are auto-approved on submit.
              Add a Condition Set to route submissions for approval.
            </div>
          )}

          {chain.condition_sets.map((set, i) => (
            <ConditionSetCard
              key={set.id}
              chain={chain}
              set={set}
              index={i}
              total={chain.condition_sets.length}
              users={users}
              locations={locations}
              departments={departments}
              busy={busy}
              onMutate={mutate}
              onMoveUp={() => reorderSet(set.id, -1)}
              onMoveDown={() => reorderSet(set.id, 1)}
              onDelete={() => deleteConditionSet(set.id)}
            />
          ))}

          <button
            type="button"
            disabled={busy}
            onClick={addConditionSet}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" /> Add Condition Set
          </button>
        </div>
      )}
    </div>
  );
}

const FIELD_LABEL: Record<ConditionField, string> = {
  location: "Location",
  department: "Department",
  amount: "Amount",
};

function describeCondition(
  cond: ApprovalConditionDto,
  locations: OrgDimensionDto[],
  departments: OrgDimensionDto[],
): string {
  if (cond.field === "amount") {
    const { min, max } = cond.values as { min: number; max: number | null };
    const lo = formatAmount(min);
    return max === null ? `Amount ${lo}+` : `Amount ${lo} – ${formatAmount(max)}`;
  }
  const pool = cond.field === "location" ? locations : departments;
  const ids = cond.values as string[];
  const names = ids.map((id) => pool.find((p) => p.id === id)?.name ?? id);
  return `${FIELD_LABEL[cond.field]} is one of: ${names.join(", ")}`;
}

function ConditionSetCard({
  chain,
  set,
  index,
  total,
  users,
  locations,
  departments,
  busy,
  onMutate,
  onMoveUp,
  onMoveDown,
  onDelete,
}: {
  chain: ApprovalChainDto;
  set: ApprovalConditionSetDto;
  index: number;
  total: number;
  users: UserDto[];
  locations: OrgDimensionDto[];
  departments: OrgDimensionDto[];
  busy: boolean;
  onMutate: (promise: Promise<Response>, successMsg: string) => Promise<boolean>;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}) {
  const [editingLevelId, setEditingLevelId] = useState<string | null>(null);
  const base = `/api/approval-chains/${chain.id}/condition-sets/${set.id}`;

  async function deleteCondition(conditionId: string) {
    await onMutate(
      fetchWithAuth(`${base}/conditions/${conditionId}`, { method: "DELETE" }),
      "Condition removed",
    );
  }

  async function saveLevel(levelId: string, body: UpdateApprovalLevelRequest) {
    const ok = await onMutate(
      fetchWithAuth(`${base}/levels/${levelId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
      "Level saved",
    );
    if (ok) setEditingLevelId(null);
  }

  async function deleteLevel(levelId: string) {
    await onMutate(
      fetchWithAuth(`${base}/levels/${levelId}`, { method: "DELETE" }),
      "Level removed",
    );
  }

  async function reorderLevel(levelId: string, direction: -1 | 1) {
    const ids = set.levels.map((l) => l.id);
    const idx = ids.indexOf(levelId);
    const swap = idx + direction;
    if (swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    await onMutate(
      fetchWithAuth(`${base}/levels/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ level_ids: ids }),
      }),
      "Levels reordered",
    );
  }

  return (
    <div
      className="rounded-xl border border-border bg-muted/10 p-4 space-y-4"
      aria-label={`Condition Set ${index + 1}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Condition Set {index + 1}</span>
          {set.conditions.length === 0 && (
            <span className="rounded-full bg-[var(--info)]/10 px-2 py-0.5 text-xs [color:var(--info)]">
              matches everything
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={busy || index === 0}
            onClick={onMoveUp}
            aria-label="Move set up"
            className="inline-flex p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={busy || index === total - 1}
            onClick={onMoveDown}
            aria-label="Move set down"
            className="inline-flex p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onDelete}
            aria-label="Delete set"
            className="inline-flex p-1 text-destructive hover:text-destructive/80 disabled:opacity-30"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Conditions */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">Conditions (all must match)</p>
        {set.conditions.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No conditions — this set matches everything.
          </p>
        )}
        <ul className="space-y-1">
          {set.conditions.map((cond) => (
            <li
              key={cond.id}
              className="flex items-center justify-between rounded-md border border-border bg-background px-2 py-1 text-sm"
            >
              <span>{describeCondition(cond, locations, departments)}</span>
              <button
                type="button"
                disabled={busy}
                onClick={() => deleteCondition(cond.id)}
                aria-label="Remove condition"
                className="inline-flex p-1 text-destructive hover:text-destructive/80 disabled:opacity-30"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
        <AddConditionForm
          base={base}
          isValueBearing={chain.is_value_bearing}
          locations={locations}
          departments={departments}
          busy={busy}
          onAdd={(promise) => onMutate(promise, "Condition added")}
        />
      </div>

      {/* Levels */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">Approval levels (in order)</p>
        {set.levels.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No levels — a matched set with no levels auto-approves.
          </p>
        )}
        {set.levels.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="text-left">
                <th className="py-1 pr-3 font-medium">Level</th>
                <th className="py-1 pr-3 font-medium">Approver</th>
                <th className="py-1 pr-3 font-medium">SLA</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {set.levels.map((l, i) =>
                editingLevelId === l.id ? (
                  <LevelEditRow
                    key={l.id}
                    level={l}
                    users={users}
                    busy={busy}
                    onSave={(body) => saveLevel(l.id, body)}
                    onCancel={() => setEditingLevelId(null)}
                  />
                ) : (
                  <tr key={l.id} className="border-t border-border">
                    <td className="py-1 pr-3 tabular-nums">{l.level}</td>
                    <td className="py-1 pr-3">{l.approver_user.name}</td>
                    <td className="py-1 pr-3 text-muted-foreground text-xs">
                      {l.nudge_after_hours ? `nudge ${l.nudge_after_hours}h` : "—"}
                      {l.escalate_after_hours ? `, escalate ${l.escalate_after_hours}h` : ""}
                    </td>
                    <td className="py-1 text-right whitespace-nowrap">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setEditingLevelId(l.id)}
                        className="inline-flex p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                        aria-label="Edit level"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={busy || i === 0}
                        onClick={() => reorderLevel(l.id, -1)}
                        className="inline-flex p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                        aria-label="Move level up"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={busy || i === set.levels.length - 1}
                        onClick={() => reorderLevel(l.id, 1)}
                        className="inline-flex p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                        aria-label="Move level down"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => deleteLevel(l.id)}
                        className="inline-flex p-1 text-destructive hover:text-destructive/80 disabled:opacity-30"
                        aria-label="Delete level"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
        <AddLevelForm
          base={base}
          users={users}
          busy={busy}
          onAdd={(promise) => onMutate(promise, "Level added")}
        />
      </div>
    </div>
  );
}

function AddConditionForm({
  base,
  isValueBearing,
  locations,
  departments,
  busy,
  onAdd,
}: {
  base: string;
  isValueBearing: boolean;
  locations: OrgDimensionDto[];
  departments: OrgDimensionDto[];
  busy: boolean;
  onAdd: (promise: Promise<Response>) => void;
}) {
  const [field, setField] = useState<ConditionField>("location");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");

  const pool = field === "location" ? locations : departments;

  function reset() {
    setSelectedIds([]);
    setAmountMin("");
    setAmountMax("");
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (field === "amount") {
      const toCents = (v: string): number | null =>
        v.trim() === "" ? null : Math.round(parseFloat(v) * 100);
      const min = toCents(amountMin);
      if (min === null || Number.isNaN(min)) {
        toast.error("Enter an amount minimum.");
        return;
      }
      onAdd(
        fetchWithAuth(`${base}/conditions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ field: "amount", values: { min, max: toCents(amountMax) } }),
        }),
      );
    } else {
      if (selectedIds.length === 0) {
        toast.error("Select at least one value.");
        return;
      }
      onAdd(
        fetchWithAuth(`${base}/conditions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ field, values: selectedIds }),
        }),
      );
    }
    reset();
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border border-dashed border-border p-3 space-y-2"
      aria-label="Add condition"
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1 text-xs">
          <span className="text-muted-foreground">Field</span>
          <select
            aria-label="Condition field"
            value={field}
            onChange={(e) => {
              setField(e.target.value as ConditionField);
              reset();
            }}
            className="block w-36 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          >
            <option value="location">Location</option>
            <option value="department">Department</option>
            {isValueBearing && <option value="amount">Amount</option>}
          </select>
        </label>

        {field === "amount" ? (
          <>
            <label className="space-y-1 text-xs">
              <span className="text-muted-foreground">Amount min</span>
              <input
                aria-label="Amount min"
                type="number"
                min="0"
                step="0.01"
                value={amountMin}
                onChange={(e) => setAmountMin(e.target.value)}
                className="block w-28 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              />
            </label>
            <label className="space-y-1 text-xs">
              <span className="text-muted-foreground">Amount max</span>
              <input
                aria-label="Amount max"
                type="number"
                min="0"
                step="0.01"
                placeholder="open-ended"
                value={amountMax}
                onChange={(e) => setAmountMax(e.target.value)}
                className="block w-28 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              />
            </label>
          </>
        ) : (
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Is one of</span>
            <select
              multiple
              aria-label={field === "location" ? "Locations" : "Departments"}
              value={selectedIds}
              onChange={(e) =>
                setSelectedIds(Array.from(e.target.selectedOptions).map((o) => o.value))
              }
              className="block w-56 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            >
              {pool.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted/50 disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" /> Add condition
        </button>
      </div>
    </form>
  );
}

function LevelEditRow({
  level,
  users,
  busy,
  onSave,
  onCancel,
}: {
  level: ApprovalLevelDto;
  users: UserDto[];
  busy: boolean;
  onSave: (body: UpdateApprovalLevelRequest) => void;
  onCancel: () => void;
}) {
  const toHoursInput = (h: number | null): string => (h === null ? "" : String(h));
  const [approverId, setApproverId] = useState(level.approver_user_id);
  const [nudge, setNudge] = useState(toHoursInput(level.nudge_after_hours));
  const [escalate, setEscalate] = useState(toHoursInput(level.escalate_after_hours));

  // The selected approver may be inactive (and so absent from `users`); keep it
  // selectable so an SLA edit doesn't silently swap the approver.
  const approverOptions = users.some((u) => u.id === approverId)
    ? users
    : [
        { id: approverId, name: level.approver_user.name } as Pick<UserDto, "id" | "name">,
        ...users,
      ];

  function submit() {
    if (!approverId) {
      toast.error("Choose an approver.");
      return;
    }
    const toHours = (v: string): number | null => (v.trim() === "" ? null : parseInt(v, 10));
    onSave({
      approver_user_id: approverId,
      nudge_after_hours: toHours(nudge),
      escalate_after_hours: toHours(escalate),
    });
  }

  return (
    <tr className="border-t border-border bg-muted/20">
      <td className="py-1 pr-3 tabular-nums">{level.level}</td>
      <td colSpan={3} className="py-2">
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Approver</span>
            <select
              aria-label="Approver"
              value={approverId}
              onChange={(e) => setApproverId(e.target.value)}
              className="block w-40 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            >
              {approverOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Nudge (hrs)</span>
            <input
              aria-label="Nudge (hrs)"
              type="number"
              min="0"
              value={nudge}
              onChange={(e) => setNudge(e.target.value)}
              className="block w-20 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Escalate (hrs)</span>
            <input
              aria-label="Escalate (hrs)"
              type="number"
              min="0"
              value={escalate}
              onChange={(e) => setEscalate(e.target.value)}
              className="block w-20 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            />
          </label>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={busy}
              onClick={submit}
              aria-label="Save level"
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" /> Save
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onCancel}
              aria-label="Cancel edit"
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted/50 disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

function AddLevelForm({
  base,
  users,
  busy,
  onAdd,
}: {
  base: string;
  users: UserDto[];
  busy: boolean;
  onAdd: (promise: Promise<Response>) => void;
}) {
  const [approverId, setApproverId] = useState("");
  const [nudge, setNudge] = useState("");
  const [escalate, setEscalate] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!approverId) {
      toast.error("Choose an approver.");
      return;
    }
    const toHours = (v: string): number | null => (v.trim() === "" ? null : parseInt(v, 10));
    onAdd(
      fetchWithAuth(`${base}/levels`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          approver_user_id: approverId,
          nudge_after_hours: toHours(nudge),
          escalate_after_hours: toHours(escalate),
        }),
      }),
    );
    setApproverId("");
    setNudge("");
    setEscalate("");
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border border-dashed border-border p-3"
      aria-label="Add level"
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1 text-xs">
          <span className="text-muted-foreground">Approver</span>
          <select
            aria-label="New level approver"
            value={approverId}
            onChange={(e) => setApproverId(e.target.value)}
            className="block w-40 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          >
            <option value="">Select…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1 text-xs">
          <span className="text-muted-foreground">Nudge (hrs)</span>
          <input
            type="number"
            min="0"
            value={nudge}
            onChange={(e) => setNudge(e.target.value)}
            className="block w-20 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          />
        </label>
        <label className="space-y-1 text-xs">
          <span className="text-muted-foreground">Escalate (hrs)</span>
          <input
            type="number"
            min="0"
            value={escalate}
            onChange={(e) => setEscalate(e.target.value)}
            className="block w-20 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          />
        </label>
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" /> Add level
        </button>
      </div>
    </form>
  );
}
